import React, { useState, useRef } from 'react';
import { Box, TextField, Typography, Button as PriButton, Grid, FormControl } from '@mui/material';
import { useDownloaduserBulkUpldTemplate, useUploadUserBulkUpldTemplate } from '../../hooks/Users';
import useShowToast from '../../hooks/useShowToast';
import useShowError from '../../hooks/useShowError';
import { ArrowBackOutlined } from '@mui/icons-material';
import { Wrapper } from './UserBulkUpload.styles';
import Button from "../Button/Button"
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

export interface StepNavigationProps {
  onCancel: () => void
}

const ERROR_MESSAGES = {
  NETWORK: "Network error, please check your connection.",
  VALIDATION: "Validation error",
  SERVER_500: "Server error. Please try again later.",
};

const UserBulkUpload: React.FC<StepNavigationProps> = ({onCancel}) => {
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [bulkError, setBulkError] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const showToast = useShowToast();
  const showError = useShowError();

  const downloadTemplate = useDownloaduserBulkUpldTemplate();
  const uploadTemplate = useUploadUserBulkUpldTemplate();
  const isUploading = uploadTemplate.isPending;

  // --- Handlers ---

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    setFileName(selected ? selected.name : '');
    setFile(selected);
    if (selected) setBulkError(false);
  };

  const handleTemplateDownload = () => {
    downloadTemplate.mutate(undefined, {
      onSuccess: (response) => {
        showToast("Template has been successfully downloaded.");
        const url = window.URL.createObjectURL(response);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Users Upload Template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      onError: () => {
        showError("Something went wrong, please try again.");
      },
    });
  };

  const handleFileUpload = (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setBulkError(true);
      return;
    }
    uploadTemplate.mutate(file, {
      onSuccess: () => {
        showToast(`File "${file.name}" uploaded successfully!`);
      },
      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          showError(ERROR_MESSAGES.NETWORK);
        } else if (error?.body?.detail && Array.isArray(error.body.detail)) {
          const messages = error.body.detail.map((item: any) => item.msg).join('\n');
          showError(`${ERROR_MESSAGES.VALIDATION}: ${messages}`);
        } else {
          const detail = error?.body?.detail || error.message || ERROR_MESSAGES.SERVER_500;
          showError(detail);
        }
      },
    });
  };

  const handleCancel = ()=>{
    onCancel()
  }

  return (
    <Wrapper>
      {/* Header */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h5" sx={{ fontSize: '24px',
            fontWeight: 700,
            color: '#111',
            lineHeight: "normal", }}>
            Bulk Upload
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "var(--brand-text-sec)",
              fontWeight: 400,
              fontSize: 14, mt: 0.5, mb: 2 }}>
            Import multiple users via template
          </Typography>
        </Box>
        <PriButton
          variant="outlined"
          startIcon={<ArrowBackOutlined />}
          onClick={handleCancel}
        >
          Existing users
        </PriButton>
      </Box>

      {/* Download Template */}
      <Grid container sx={{ mb: 2 }}>
        <Grid item>
           <PriButton variant="contained" color="secondary" onClick={handleTemplateDownload} startIcon={<FileDownloadOutlinedIcon/>}>
              Download template
          </PriButton>
        </Grid>
      </Grid>

      {/* File Upload */}
      <form onSubmit={handleFileUpload}>
        <Grid container sx={{ mb: 2 }} alignItems="center">
          <Grid item>
            <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }}>
              <TextField
                label="File path"
                variant="outlined"
                value={fileName}
                InputProps={{ readOnly: true }}
                size="small"
                sx={{ minWidth: 264 }}
                error={bulkError}
                helperText={bulkError ? "Please select a file before submitting." : ""}
              />
              <input
                type="file"
                hidden
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                ref={inputRef}
                onChange={handleFileSelect}
              />
            </FormControl>
          </Grid>
          <Grid item>
            <PriButton sx={{marginLeft: '20px'}} variant="outlined" onClick={() => inputRef.current?.click()} startIcon={<FileUploadOutlinedIcon />}>
                Browse
            </PriButton>

          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <PriButton
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </PriButton>
          <PriButton
            variant="contained"
            type="submit"
            disabled={isUploading}
          >
            Upload
          </PriButton>

        </Box>
      </form>
    </Wrapper>
  );
};

export default UserBulkUpload;

